import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from "recharts";
import {
  TrendingUp, Dumbbell, Plus, Loader2, ChevronDown,
  Target, TrendingDown, ArrowUpRight, ArrowDownRight, Minus, Check, X,
  Calendar,
} from "lucide-react";
import { useUserAuth, getUserToken } from "../context/UserAuthContext";
import UserNav from "../components/UserNav";

const API = process.env.REACT_APP_BACKEND_URL;

const fmt = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
};

// --- Weekly grouping helpers ---
function getWeekStart(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diff);
  return monday.toISOString().slice(0, 10);
}

function buildWeeklyData(records) {
  if (records.length < 2) return [];
  const weeks = {};
  for (const r of records) {
    const wk = getWeekStart(r.fecha);
    if (!weeks[wk]) weeks[wk] = [];
    weeks[wk].push(r);
  }
  const keys = Object.keys(weeks).sort();
  const rows = keys.map((wk) => {
    const last = weeks[wk][weeks[wk].length - 1];
    const d = new Date(wk + "T00:00:00");
    return {
      weekKey: wk,
      label: `${d.toLocaleDateString("es-ES", { day: "numeric", month: "short" })}`,
      peso: last.peso_kg,
    };
  });
  return rows.map((r, i) => ({
    ...r,
    cambio: i === 0 ? null : +(r.peso - rows[i - 1].peso).toFixed(1),
  }));
}

function weeksBetween(a, b) {
  const msPerWeek = 7 * 24 * 60 * 60 * 1000;
  return Math.max(1, Math.ceil((new Date(b) - new Date(a)) / msPerWeek));
}

export default function MyEvolutionPage() {
  const { user, loading, logout } = useUserAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("peso");

  // Weight data
  const [weightHistory, setWeightHistory] = useState([]);
  const [weightLoading, setWeightLoading] = useState(true);
  const [newPeso, setNewPeso] = useState("");
  const [newNotas, setNewNotas] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveOk, setSaveOk] = useState(false);
  const [saveErr, setSaveErr] = useState("");

  // Goal
  const [goal, setGoal] = useState(null);
  const [goalInput, setGoalInput] = useState("");
  const [goalEditing, setGoalEditing] = useState(false);
  const [savingGoal, setSavingGoal] = useState(false);
  const [deletingGoal, setDeletingGoal] = useState(false);

  // Exercise data (legacy — not used in new rutina view)
  const [exercises, setExercises] = useState([]);
  const [selectedEx, setSelectedEx] = useState("");
  const [exHistory, setExHistory] = useState([]);
  const [exLoading, setExLoading] = useState(false);

  // Rutina progress view
  const [rutinaData, setRutinaData] = useState(null);
  const [rutinaLoading, setRutinaLoading] = useState(true);
  const [selectedDia, setSelectedDia] = useState(1);

  // Historial view
  const [allHistoricExs, setAllHistoricExs] = useState([]);
  const [allExsLoading, setAllExsLoading] = useState(false);
  const [historicEx, setHistoricEx] = useState(null);  // selected exercise name
  const [historicHistory, setHistoricHistory] = useState([]);
  const [historicLoading, setHistoricLoading] = useState(false);

  // Weekly weight reminder
  const [weightReminderDismissed, setWeightReminderDismissed] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login", { replace: true }); return; }
    loadWeight();
    loadGoal();
    loadRutinaProgreso();
  }, [user, loading]); // eslint-disable-line

  // Load exercise list lazily when Historial tab opens
  useEffect(() => {
    if (tab === "historial" && allHistoricExs.length === 0 && !allExsLoading) {
      loadAllHistoricExs();
    }
  }, [tab]); // eslint-disable-line

  useEffect(() => {
    if (selectedEx) loadExHistory(selectedEx);
  }, [selectedEx]);

  const headers = { Authorization: `Bearer ${getUserToken()}` };

  const loadWeight = async () => {
    setWeightLoading(true);
    try {
      const r = await fetch(`${API}/api/evolution/mi-historial`, { headers: { Authorization: `Bearer ${getUserToken()}` } });
      const d = await r.json();
      setWeightHistory(Array.isArray(d) ? d : []);
    } catch { setWeightHistory([]); }
    finally { setWeightLoading(false); }
  };

  const loadGoal = async () => {
    try {
      const r = await fetch(`${API}/api/evolution/mi-meta`, { headers });
      const d = await r.json();
      setGoal(d.objetivo_peso_kg ?? null);
      if (d.objetivo_peso_kg) setGoalInput(String(d.objetivo_peso_kg));
    } catch { setGoal(null); }
  };

  const loadRutinaProgreso = async () => {
    setRutinaLoading(true);
    try {
      const r = await fetch(`${API}/api/evolution/rutina-progreso`, { headers: { Authorization: `Bearer ${getUserToken()}` } });
      const d = await r.json();
      setRutinaData(d);
      if (d.dias?.length > 0) setSelectedDia(d.dias[0].dia);
    } catch { setRutinaData(null); }
    finally { setRutinaLoading(false); }
  };

  const loadAllHistoricExs = async () => {
    setAllExsLoading(true);
    try {
      const r = await fetch(`${API}/api/evolution/mi-entrenos`, { headers: { Authorization: `Bearer ${getUserToken()}` } });
      const d = await r.json();
      setAllHistoricExs(Array.isArray(d) ? d : []);
    } catch { setAllHistoricExs([]); }
    finally { setAllExsLoading(false); }
  };

  const loadHistoricEx = async (nombre) => {
    setHistoricEx(nombre);
    setHistoricLoading(true);
    setHistoricHistory([]);
    try {
      const r = await fetch(`${API}/api/evolution/mi-entreno/${encodeURIComponent(nombre)}`, { headers: { Authorization: `Bearer ${getUserToken()}` } });
      const d = await r.json();
      setHistoricHistory(Array.isArray(d) ? d : []);
    } catch { setHistoricHistory([]); }
    finally { setHistoricLoading(false); }
  };

  const loadExHistory = async (name) => {
    setExLoading(true);
    try {
      const r = await fetch(`${API}/api/evolution/mi-entreno/${encodeURIComponent(name)}`, { headers });
      const d = await r.json();
      setExHistory(Array.isArray(d) ? d : []);
    } catch { setExHistory([]); }
    finally { setExLoading(false); }
  };

  const handleAddWeight = async (e) => {
    e.preventDefault();
    if (!newPeso) return;
    setSaving(true); setSaveOk(false); setSaveErr("");
    try {
      const res = await fetch(`${API}/api/evolution/registro`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({ peso_kg: parseFloat(newPeso), notas: newNotas || null }),
      });
      if (!res.ok) { const d = await res.json(); setSaveErr(d.detail || "Error"); return; }
      setNewPeso(""); setNewNotas(""); setSaveOk(true);
      await loadWeight();
      setTimeout(() => setSaveOk(false), 3000);
    } catch { setSaveErr("Error de conexión."); }
    finally { setSaving(false); }
  };

  const handleSaveGoal = async () => {
    if (!goalInput) return;
    setSavingGoal(true);
    try {
      const res = await fetch(`${API}/api/evolution/mi-meta`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({ objetivo_peso_kg: parseFloat(goalInput) }),
      });
      if (res.ok) { setGoal(parseFloat(goalInput)); setGoalEditing(false); }
    } catch { /* ignore */ }
    finally { setSavingGoal(false); }
  };

  const handleDeleteGoal = async () => {
    setDeletingGoal(true);
    try {
      await fetch(`${API}/api/evolution/mi-meta`, { method: "DELETE", headers });
      setGoal(null); setGoalInput(""); setGoalEditing(false);
    } catch { /* ignore */ }
    finally { setDeletingGoal(false); }
  };

  const handleLogout = () => { logout(); navigate("/login", { replace: true }); };

  const dismissWeightReminder = () => {
    localStorage.setItem("peso_reminder_dismissed", new Date().toISOString().slice(0, 10));
    setWeightReminderDismissed(true);
  };

  // Computed stats
  const startWeight = weightHistory.length > 0 ? weightHistory[0].peso_kg : null;
  const currentWeight = weightHistory.length > 0 ? weightHistory[weightHistory.length - 1].peso_kg : null;
  const totalChange = startWeight != null && currentWeight != null ? +(currentWeight - startWeight).toFixed(1) : null;
  const weeksActive = weightHistory.length >= 2
    ? weeksBetween(weightHistory[0].fecha, weightHistory[weightHistory.length - 1].fecha)
    : null;

  const weeklyData = buildWeeklyData(weightHistory);
  const last8Weeks = weeklyData.slice(-8);

  // Chart data with goal domain
  const chartData = weightHistory.map((r) => ({ fecha: fmt(r.fecha), peso: r.peso_kg }));
  const allWeights = weightHistory.map((r) => r.peso_kg);
  if (goal) allWeights.push(goal);
  const yMin = allWeights.length ? Math.floor(Math.min(...allWeights) - 2) : "auto";
  const yMax = allWeights.length ? Math.ceil(Math.max(...allWeights) + 2) : "auto";

  // Goal indicator
  const goalRemaining = goal != null && currentWeight != null ? +(currentWeight - goal).toFixed(1) : null;
  const goalReached = goal != null && currentWeight != null && currentWeight <= goal + 0.2 && currentWeight >= goal - 0.2;
  const goalLosing = goal != null && currentWeight != null && goal < currentWeight; // objetivo es perder
  const goalGaining = goal != null && currentWeight != null && goal > currentWeight; // objetivo es ganar

  // Weekly weight reminder: show if no entries or last entry > 7 days ago, and not dismissed recently
  const showWeightReminder = !weightReminderDismissed && !weightLoading && (() => {
    const dismissed = localStorage.getItem("peso_reminder_dismissed");
    if (dismissed) {
      const daysSince = (Date.now() - new Date(dismissed + "T00:00:00").getTime()) / (1000 * 60 * 60 * 24);
      if (daysSince < 7) return false;
    }
    if (weightHistory.length === 0) return true;
    const last = weightHistory[weightHistory.length - 1];
    const daysSinceEntry = (Date.now() - new Date(last.fecha + "T00:00:00").getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceEntry >= 7;
  })();

  // Exercise chart data
  const exChartData = exHistory.map((log) => ({
    fecha: fmt(log.fecha),
    maxPeso: Math.max(...log.series.map((s) => s.peso_kg)),
  }));

  if (loading) return <LoadingScreen />;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="my-evolution-page">
      <UserNav user={user} onLogout={handleLogout} />

      <div className="max-w-lg mx-auto px-4 py-8 pb-16">
        <div className="mb-6">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#F2C230] font-black mb-1">Tu progreso</p>
          <h1 className="text-3xl font-black">Mi Evolución</h1>
        </div>

        {/* Weekly weight reminder banner */}
        {showWeightReminder && (
          <div
            data-testid="weight-reminder-banner"
            className="flex items-start gap-3 bg-[#F2C230]/10 border border-[#F2C230]/40 px-4 py-3 mb-5"
          >
            <TrendingUp size={16} className="text-[#F2C230] shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-black text-white">Registra tu peso esta semana</p>
              <p className="text-[11px] text-white/50 mt-0.5">¡Un registro a la semana te ayuda a ver tu progreso real!</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                onClick={() => { setTab("peso"); dismissWeightReminder(); }}
                className="text-[10px] font-black tracking-[0.15em] uppercase bg-[#F2C230] text-black px-3 py-1.5 hover:bg-white transition-colors"
                data-testid="reminder-register-btn"
              >
                Registrar
              </button>
              <button
                onClick={dismissWeightReminder}
                className="text-white/30 hover:text-white transition-colors"
                data-testid="reminder-dismiss-btn"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Tab switcher */}
        <div className="flex border border-white/10 mb-8">
          {[
            { key: "peso", label: "Peso corporal", icon: <TrendingUp size={12} /> },
            { key: "ejercicios", label: "Por rutina", icon: <Calendar size={12} /> },
            { key: "historial", label: "Historial", icon: <Dumbbell size={12} /> },
          ].map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-[11px] font-black tracking-[0.15em] uppercase transition-colors ${
                tab === t.key ? "bg-[#F2C230] text-black" : "text-white/40 hover:text-white"
              }`}
            >
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        {/* ============================
            PESO CORPORAL TAB
        ============================ */}
        {tab === "peso" && (
          <div className="space-y-5" data-testid="peso-tab">

            {/* Stats cards */}
            {!weightLoading && weightHistory.length >= 2 && (
              <div className="grid grid-cols-4 gap-2" data-testid="stats-cards">
                <StatCard label="Inicio" value={startWeight != null ? `${startWeight} kg` : "—"} />
                <StatCard label="Actual" value={currentWeight != null ? `${currentWeight} kg` : "—"} highlight />
                <StatCard
                  label="Cambio"
                  value={totalChange != null ? `${totalChange > 0 ? "+" : ""}${totalChange} kg` : "—"}
                  trend={totalChange}
                />
                <StatCard label="Semanas" value={weeksActive != null ? weeksActive : "—"} />
              </div>
            )}

            {/* Goal indicator (if set) */}
            {goal != null && currentWeight != null && (
              <div
                data-testid="goal-indicator"
                className={`border px-4 py-3 flex items-center gap-3 ${
                  goalReached
                    ? "border-green-500/40 bg-green-500/8"
                    : "border-[#F2C230]/25 bg-[#F2C230]/5"
                }`}
              >
                <Target size={16} className={goalReached ? "text-green-400" : "text-[#F2C230]"} />
                <div className="flex-1 min-w-0">
                  {goalReached ? (
                    <p className="text-sm font-black text-green-300">¡Meta alcanzada!</p>
                  ) : goalLosing ? (
                    <p className="text-sm font-black text-white">
                      Te quedan <span className="text-[#F2C230]">{Math.abs(goalRemaining)} kg</span> para tu meta de {goal} kg
                    </p>
                  ) : (
                    <p className="text-sm font-black text-white">
                      <span className="text-[#F2C230]">{Math.abs(goalRemaining)} kg</span> por ganar para tu meta de {goal} kg
                    </p>
                  )}
                  {startWeight != null && goalReached && (
                    <p className="text-[11px] text-green-200/60 mt-0.5">Empezaste en {startWeight} kg</p>
                  )}
                </div>
                <button
                  onClick={() => setGoalEditing(true)}
                  className="text-white/25 hover:text-white/50 transition-colors text-[10px] uppercase tracking-wider font-bold"
                >
                  Editar
                </button>
              </div>
            )}

            {/* Weight chart */}
            {weightLoading ? (
              <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-white/30" /></div>
            ) : weightHistory.length >= 2 ? (
              <div className="border border-white/8 p-4" data-testid="weight-chart">
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40 mb-4">
                  Evolución del peso (kg){goal ? " · línea = meta" : ""}
                </p>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="fecha" tick={{ fill: "rgba(255,255,255,0.3)", fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis
                      domain={[yMin, yMax]}
                      tick={{ fill: "rgba(255,255,255,0.3)", fontSize: 10 }}
                      axisLine={false} tickLine={false}
                      tickFormatter={(v) => `${v}kg`}
                    />
                    <Tooltip
                      contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 0 }}
                      labelStyle={{ color: "rgba(255,255,255,0.5)", fontSize: 11 }}
                      itemStyle={{ color: "#F2C230", fontWeight: "bold" }}
                      formatter={(v) => [`${v} kg`, "Peso"]}
                    />
                    {goal != null && (
                      <ReferenceLine
                        y={goal}
                        stroke="#F2C230"
                        strokeDasharray="6 4"
                        strokeOpacity={0.5}
                        label={{ value: `Meta: ${goal}kg`, position: "insideTopRight", fill: "#F2C230", fontSize: 10, opacity: 0.7 }}
                      />
                    )}
                    <Line
                      type="monotone" dataKey="peso" stroke="#F2C230" strokeWidth={2}
                      dot={{ fill: "#F2C230", r: 4, strokeWidth: 0 }}
                      activeDot={{ r: 6, fill: "#fff" }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : weightHistory.length === 1 ? (
              <div className="border border-white/8 p-4 text-center text-white/30 text-sm">
                Registra al menos 2 pesos para ver la gráfica.
              </div>
            ) : (
              <div className="border border-white/8 p-4 text-center text-white/30 text-sm" data-testid="weight-empty">
                Sin registros todavía. Añade tu primer peso.
              </div>
            )}

            {/* Weekly progression table */}
            {last8Weeks.length >= 2 && (
              <div data-testid="weekly-progress">
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40 mb-3">Progreso semanal</p>
                <div className="border border-white/8 divide-y divide-white/5">
                  {last8Weeks.map((w, i) => (
                    <div key={w.weekKey} className="flex items-center justify-between px-4 py-2.5">
                      <div>
                        <p className="text-xs text-white/60 font-medium">
                          Semana del {w.label}
                          {i === last8Weeks.length - 1 && (
                            <span className="ml-1.5 text-[9px] bg-[#F2C230]/15 text-[#F2C230] px-1.5 py-0.5 uppercase tracking-wider font-black">Actual</span>
                          )}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-black text-white">{w.peso} kg</span>
                        {w.cambio != null && (
                          <span className={`flex items-center gap-0.5 text-xs font-black ${
                            w.cambio < 0 ? "text-green-400" : w.cambio > 0 ? "text-red-400" : "text-white/30"
                          }`}>
                            {w.cambio < 0 ? <ArrowDownRight size={11} /> : w.cambio > 0 ? <ArrowUpRight size={11} /> : <Minus size={11} />}
                            {w.cambio > 0 ? "+" : ""}{w.cambio} kg
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Goal form */}
            <div className="border border-white/10 p-4" data-testid="goal-form-section">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Target size={12} className="text-[#F2C230]" />
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40">
                    {goal != null ? "Meta de peso" : "Fijar meta de peso"}
                  </p>
                </div>
                {goal != null && !goalEditing && (
                  <button onClick={() => setGoalEditing(true)} data-testid="edit-goal-btn"
                    className="text-[10px] uppercase tracking-wider text-white/30 hover:text-white transition-colors font-bold">
                    Cambiar
                  </button>
                )}
              </div>

              {goal != null && !goalEditing ? (
                <div className="flex items-center justify-between">
                  <p className="text-2xl font-black text-[#F2C230]" data-testid="goal-value">{goal} kg</p>
                  <button onClick={handleDeleteGoal} disabled={deletingGoal} data-testid="delete-goal-btn"
                    className="text-[10px] uppercase tracking-wider text-white/20 hover:text-red-400 transition-colors font-bold">
                    {deletingGoal ? "..." : "Eliminar meta"}
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="number" min="30" max="300" step="0.1"
                    value={goalInput}
                    onChange={(e) => setGoalInput(e.target.value)}
                    placeholder="ej: 72.5"
                    data-testid="goal-input"
                    className="flex-1 bg-black border border-white/10 text-white placeholder:text-white/20 px-3 py-2.5 text-sm outline-none focus:border-[#F2C230] transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <button onClick={handleSaveGoal} disabled={!goalInput || savingGoal} data-testid="save-goal-btn"
                    className="px-4 py-2.5 bg-[#F2C230] text-black font-black text-[11px] tracking-[0.2em] uppercase hover:bg-white transition-colors disabled:opacity-40 flex items-center gap-1">
                    {savingGoal ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
                    {savingGoal ? "..." : "Guardar"}
                  </button>
                  {goal != null && (
                    <button onClick={() => setGoalEditing(false)} className="px-3 text-white/30 hover:text-white transition-colors">
                      <X size={14} />
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Add weight form */}
            <div className="border border-[#F2C230]/30 bg-[#F2C230]/5 p-4" data-testid="add-weight-form">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-0.5 h-5 bg-[#F2C230]" />
                <p className="text-[11px] tracking-[0.2em] uppercase font-black text-[#F2C230]">Registrar peso corporal</p>
              </div>
              <form onSubmit={handleAddWeight} className="space-y-3">
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-[10px] text-white/60 uppercase tracking-widest mb-1.5">Peso (kg)</label>
                    <input type="number" min="20" max="300" step="0.1" value={newPeso}
                      onChange={(e) => setNewPeso(e.target.value)} placeholder="75.0" required
                      data-testid="new-weight-input"
                      className="w-full bg-white/8 border border-white/30 text-white placeholder:text-white/35 px-3 py-3 text-base font-black outline-none focus:border-[#F2C230] transition-colors [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                  <button type="submit" disabled={!newPeso || saving} data-testid="save-weight-btn"
                    className="self-end px-5 py-3 bg-[#F2C230] text-black font-black text-[11px] tracking-[0.2em] uppercase hover:bg-white transition-colors disabled:opacity-40 flex items-center gap-1.5">
                    {saving ? <Loader2 size={12} className="animate-spin" /> : <Plus size={13} />}
                    {saving ? "..." : "Guardar"}
                  </button>
                </div>
                <textarea value={newNotas} onChange={(e) => setNewNotas(e.target.value)}
                  placeholder="Notas opcionales (ej: después del entreno, en ayunas...)"
                  data-testid="weight-notas-input"
                  className="w-full bg-white/8 border border-white/20 text-white placeholder:text-white/35 px-3 py-2.5 text-sm outline-none focus:border-[#F2C230] transition-colors resize-none h-14"
                />
                {saveErr && <p className="text-red-400 text-xs" data-testid="weight-error">{saveErr}</p>}
                {saveOk && <p className="text-green-400 text-xs font-black" data-testid="weight-ok">Registro guardado</p>}
              </form>
            </div>

            {/* History list */}
            {weightHistory.length > 0 && (
              <div data-testid="weight-history">
                <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-white/40 mb-3">Historial completo</p>
                <div className="border border-white/8 divide-y divide-white/5">
                  {[...weightHistory].reverse().slice(0, 20).map((r, i) => (
                    <div key={r.id || i} className="flex items-center justify-between px-4 py-3">
                      <div>
                        <p className="text-sm font-black text-white">{r.peso_kg} kg</p>
                        {r.notas && <p className="text-[11px] text-white/35 mt-0.5">{r.notas}</p>}
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-white/40">{fmt(r.fecha)}</p>
                        {r.fuente === "staff" && <p className="text-[10px] text-[#F2C230]/60 mt-0.5">Staff</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ============================
            POR RUTINA TAB
        ============================ */}
        {tab === "ejercicios" && (
          <div className="space-y-5" data-testid="ejercicios-tab">
            {rutinaLoading ? (
              <div className="flex justify-center py-10">
                <Loader2 size={20} className="animate-spin text-white/30" />
              </div>
            ) : !rutinaData?.dias?.length ? (
              <div className="border border-white/8 p-8 text-center" data-testid="exercises-empty">
                <Dumbbell size={32} className="mx-auto text-white/15 mb-3" />
                <p className="text-white/35 text-sm">No tienes una rutina activa.</p>
                <p className="text-white/20 text-xs mt-1">Habla con tu entrenador o crea tu propia rutina.</p>
              </div>
            ) : (
              <>
                {/* Routine name */}
                <div className="flex items-center gap-2.5">
                  <div className="w-0.5 h-5 bg-[#F2C230]" />
                  <p className="text-sm font-black text-white">{rutinaData.rutina_nombre}</p>
                </div>

                {/* Day tabs */}
                <div
                  className="flex gap-1.5 overflow-x-auto pb-0.5"
                  style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
                  data-testid="dia-tabs"
                >
                  {rutinaData.dias.map((d) => (
                    <button
                      key={d.dia}
                      onClick={() => setSelectedDia(d.dia)}
                      data-testid={`dia-tab-${d.dia}`}
                      className={`shrink-0 px-5 py-2 text-[11px] font-black tracking-[0.15em] uppercase transition-colors ${
                        selectedDia === d.dia
                          ? "bg-[#F2C230] text-black"
                          : "border border-white/15 text-white/40 hover:text-white hover:border-white/40"
                      }`}
                    >
                      Día {d.dia}
                    </button>
                  ))}
                </div>

                {/* Exercise cards for selected day */}
                {rutinaData.dias
                  .filter((d) => d.dia === selectedDia)
                  .map((diaData) => (
                    <div key={diaData.dia} className="space-y-3">
                      {diaData.ejercicios.length === 0 ? (
                        <p className="text-white/25 text-sm text-center py-6">Sin ejercicios en este día.</p>
                      ) : (
                        diaData.ejercicios.map((ej, i) => (
                          <ExerciseProgressCard key={i} ejercicio={ej} />
                        ))
                      )}
                    </div>
                  ))}
              </>
            )}
          </div>
        )}
        {/* ============================
            HISTORIAL TAB
        ============================ */}
        {tab === "historial" && (
          <div className="space-y-5" data-testid="historial-tab">
            {allExsLoading ? (
              <div className="flex justify-center py-10">
                <Loader2 size={20} className="animate-spin text-white/30" />
              </div>
            ) : allHistoricExs.length === 0 ? (
              <div className="border border-white/8 p-8 text-center">
                <Dumbbell size={32} className="mx-auto text-white/15 mb-3" />
                <p className="text-white/35 text-sm">Sin registros de entrenamiento todavía.</p>
                <p className="text-white/20 text-xs mt-1">Registra pesos durante tus entrenamientos para ver la evolución aquí.</p>
              </div>
            ) : (
              <>
                {/* Exercise grid */}
                <div>
                  <p className="text-[10px] font-black tracking-[0.2em] uppercase text-white/35 mb-3">
                    {allHistoricExs.length} ejercicio{allHistoricExs.length !== 1 ? "s" : ""} registrado{allHistoricExs.length !== 1 ? "s" : ""}
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {allHistoricExs.map((ex) => (
                      <button
                        key={ex.ejercicio_nombre}
                        onClick={() => loadHistoricEx(ex.ejercicio_nombre)}
                        data-testid={`historic-ex-${ex.ejercicio_nombre.replace(/\s+/g, "-").toLowerCase()}`}
                        className={`text-left p-3 border transition-colors ${
                          historicEx === ex.ejercicio_nombre
                            ? "border-[#F2C230] bg-[#F2C230]/5"
                            : "border-white/10 hover:border-white/30"
                        }`}
                      >
                        <p className="text-xs font-black text-white leading-snug line-clamp-2">{ex.ejercicio_nombre}</p>
                        <p className="text-[10px] text-white/30 mt-1">
                          {ex.sesiones} sesión{ex.sesiones !== 1 ? "es" : ""}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Detail: chart + sessions */}
                {historicEx && (
                  <div className="mt-2" data-testid="historic-detail">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="w-0.5 h-5 bg-[#F2C230]" />
                        <p className="text-sm font-black text-white">{historicEx}</p>
                      </div>
                      <button
                        onClick={() => { setHistoricEx(null); setHistoricHistory([]); }}
                        className="text-white/25 hover:text-white transition-colors"
                      >
                        <X size={16} />
                      </button>
                    </div>

                    {historicLoading ? (
                      <div className="flex justify-center py-8">
                        <Loader2 size={18} className="animate-spin text-white/30" />
                      </div>
                    ) : historicHistory.length === 0 ? (
                      <p className="text-white/30 text-sm text-center py-6">Sin datos disponibles.</p>
                    ) : (
                      <HistoricExerciseDetail history={historicHistory} />
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

// ---- Sub-components ----

function ExerciseProgressCard({ ejercicio }) {
  const {
    nombre, series_objetivo, reps_objetivo,
    sesiones, ini_peso, act_peso, progreso, total_sesiones,
  } = ejercicio;

  const hasSessions = total_sesiones > 0;
  const chartData = sesiones.slice(-20).map((s) => ({
    fecha: new Date(s.fecha + "T00:00:00").toLocaleDateString("es-ES", { day: "numeric", month: "short" }),
    peso: s.max_peso,
  }));
  const hasChart = chartData.length >= 1;  // Show even for a single session
  const hasStats = chartData.length >= 2;  // Stats only meaningful with 2+ points
  const lastSession = sesiones[sesiones.length - 1];

  return (
    <div className="border border-white/10 overflow-hidden" data-testid={`exercise-card-${nombre.replace(/\s+/g, "-").toLowerCase()}`}>
      {/* Header */}
      <div className="px-4 py-3 flex items-start justify-between gap-3 bg-white/[0.02]">
        <div className="min-w-0">
          <p className="text-sm font-black text-white leading-tight truncate">{nombre}</p>
          {(series_objetivo || reps_objetivo) && (
            <p className="text-[10px] text-white/30 mt-0.5">
              Objetivo: {series_objetivo ?? "—"} × {reps_objetivo ?? "—"} reps
            </p>
          )}
        </div>
        <span
          className={`shrink-0 text-[10px] font-black tracking-wider mt-0.5 ${
            hasSessions ? "text-[#F2C230]" : "text-white/20"
          }`}
        >
          {hasSessions ? `${total_sesiones} sesión${total_sesiones !== 1 ? "es" : ""}` : "Sin registros"}
        </span>
      </div>

      {/* Chart */}
      {hasSessions && hasChart && (
        <div className="px-2 pt-3 pb-0">
          <ResponsiveContainer width="100%" height={110}>
            <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
              <XAxis
                dataKey="fecha"
                tick={{ fill: "rgba(255,255,255,0.22)", fontSize: 9 }}
                axisLine={false} tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={["auto", "auto"]}
                tick={{ fill: "rgba(255,255,255,0.22)", fontSize: 9 }}
                axisLine={false} tickLine={false}
                tickFormatter={(v) => `${v}kg`}
                width={36}
              />
              <Tooltip
                contentStyle={{
                  background: "#111",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 0,
                  padding: "4px 8px",
                }}
                labelStyle={{ color: "rgba(255,255,255,0.4)", fontSize: 10 }}
                itemStyle={{ color: "#F2C230", fontWeight: "bold", fontSize: 11 }}
                formatter={(v) => [`${v} kg`, "Peso máx"]}
              />
              <Line
                type="monotone"
                dataKey="peso"
                stroke="#F2C230"
                strokeWidth={2}
                dot={{ fill: "#F2C230", r: 3, strokeWidth: 0 }}
                activeDot={{ r: 5, fill: "#fff" }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Stats footer */}
      {hasSessions && (
        <div className="px-4 py-3 border-t border-white/5">
          {hasStats ? (
            <div className="flex items-center gap-3">
              <div>
                <p className="text-[9px] uppercase tracking-widest text-white/25 font-bold">Inicio</p>
                <p className="text-xs font-black text-white/50">{ini_peso ?? "—"}kg</p>
              </div>
              <div className="flex-1 h-px bg-white/8" />
              <div className="text-right">
                <p className="text-[9px] uppercase tracking-widest text-white/25 font-bold">Actual</p>
                <p className="text-xs font-black text-[#F2C230]">{act_peso ?? "—"}kg</p>
              </div>
              {progreso !== null && (
                <span
                  className={`ml-1 text-xs font-black px-2 py-0.5 ${
                    progreso >= 0
                      ? "text-green-400 bg-green-400/8"
                      : "text-red-400 bg-red-400/8"
                  }`}
                >
                  {progreso >= 0 ? "+" : ""}
                  {progreso}kg
                </span>
              )}
            </div>
          ) : (
            /* Only 1 session: show last series detail */
            lastSession && (
              <div className="flex items-start gap-2 flex-wrap">
                <span className="text-[10px] text-white/25 uppercase tracking-wider shrink-0 mt-0.5">Último:</span>
                <div className="flex gap-1.5 flex-wrap">
                  {lastSession.series.map((s, i) => (
                    <span
                      key={i}
                      className="text-[11px] text-[#F2C230]/80 bg-[#F2C230]/6 border border-[#F2C230]/10 px-2 py-0.5"
                    >
                      {s.reps}×{s.peso_kg}kg
                    </span>
                  ))}
                </div>
              </div>
            )
          )}
        </div>
      )}

      {/* No sessions yet */}
      {!hasSessions && (
        <div className="px-4 py-3 flex items-center gap-2 text-white/15 border-t border-white/5">
          <TrendingUp size={12} />
          <span className="text-[11px]">Completa tu primer entreno para ver el progreso</span>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, highlight, trend }) {
  const trendColor = trend == null ? "" : trend < 0 ? "text-green-400" : trend > 0 ? "text-red-400" : "text-white/50";
  return (
    <div className={`border px-3 py-3 text-center ${highlight ? "border-[#F2C230]/30 bg-[#F2C230]/5" : "border-white/8"}`}>
      <p className="text-[9px] uppercase tracking-widest text-white/30 font-bold mb-1">{label}</p>
      <p className={`text-sm font-black ${highlight ? "text-[#F2C230]" : trend != null ? trendColor : "text-white"}`}>{value}</p>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen bg-black flex items-center justify-center" data-testid="evolution-loading">
      <div className="w-8 h-8 border-2 border-[#F2C230] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function HistoricExerciseDetail({ history }) {
  // history: [{id, fecha, series:[{reps, peso_kg}], notas, ...}] sorted ASC
  const chartData = history.slice(-24).map((log) => {
    const valid = log.series.filter((s) => s.peso_kg > 0);
    const maxPeso = valid.length ? Math.max(...valid.map((s) => s.peso_kg)) : 0;
    return {
      fecha: new Date(log.fecha + "T00:00:00").toLocaleDateString("es-ES", { day: "numeric", month: "short" }),
      peso: maxPeso,
    };
  });

  const pesos = chartData.map((d) => d.peso).filter(Boolean);
  const ini = pesos[0] ?? null;
  const act = pesos[pesos.length - 1] ?? null;
  const prog = ini != null && act != null && pesos.length >= 2 ? +(act - ini).toFixed(1) : null;

  return (
    <div className="space-y-4">
      {/* Stats row */}
      {prog !== null && (
        <div className="grid grid-cols-3 gap-2">
          <StatCard label="Inicio" value={`${ini}kg`} />
          <StatCard label="Actual" value={`${act}kg`} highlight />
          <StatCard label="Progreso" value={`${prog >= 0 ? "+" : ""}${prog}kg`} trend={prog * -1} />
        </div>
      )}

      {/* Full chart */}
      {chartData.length >= 1 && (
        <div className="border border-white/8 pt-4 pb-2 pr-2" data-testid="historic-chart">
          <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-white/30 mb-3 px-4">
            Peso máximo por sesión (kg)
          </p>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
              <XAxis
                dataKey="fecha"
                tick={{ fill: "rgba(255,255,255,0.25)", fontSize: 9 }}
                axisLine={false} tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={["auto", "auto"]}
                tick={{ fill: "rgba(255,255,255,0.25)", fontSize: 9 }}
                axisLine={false} tickLine={false}
                tickFormatter={(v) => `${v}kg`}
                width={38}
              />
              <Tooltip
                contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 0, padding: "4px 10px" }}
                labelStyle={{ color: "rgba(255,255,255,0.4)", fontSize: 10 }}
                itemStyle={{ color: "#F2C230", fontWeight: "bold", fontSize: 12 }}
                formatter={(v) => [`${v} kg`, "Peso máx"]}
              />
              <Line
                type="monotone" dataKey="peso" stroke="#F2C230" strokeWidth={2.5}
                dot={{ fill: "#F2C230", r: 4, strokeWidth: 0 }}
                activeDot={{ r: 6, fill: "#fff" }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Session list */}
      <div data-testid="historic-session-list">
        <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-white/30 mb-2">
          {history.length} sesión{history.length !== 1 ? "es" : ""}
        </p>
        <div className="border border-white/8 divide-y divide-white/5">
          {[...history].reverse().slice(0, 15).map((log, i) => {
            const valid = log.series.filter((s) => s.peso_kg > 0);
            const maxPeso = valid.length ? Math.max(...valid.map((s) => s.peso_kg)) : null;
            return (
              <div key={log.id || i} className="px-4 py-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-black text-white">
                    {new Date(log.fecha + "T00:00:00").toLocaleDateString("es-ES", {
                      day: "numeric", month: "long", year: "numeric",
                    })}
                  </p>
                  {maxPeso && (
                    <span className="text-[10px] font-black text-[#F2C230]">{maxPeso}kg máx</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {log.series.map((s, j) => (
                    <span key={j} className="text-[11px] bg-white/5 border border-white/8 px-2 py-0.5 text-white/60">
                      {s.reps} × {s.peso_kg}kg
                    </span>
                  ))}
                </div>
                {log.notas && <p className="text-[11px] text-white/25 mt-1.5">{log.notas}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
